import React, { useState, useEffect, useContext } from "react";
import qs from "query-string";
import * as Yup from "yup";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { Formik, Form, Field } from "formik";
import { Link as RouterLink } from "react-router-dom";
import usePlans from "../../hooks/usePlans";
import { i18n } from "../../translate/i18n";
import { openApi } from "../../services/api";
import toastError from "../../errors/toastError";
import ColorModeContext from "../../layout/themeContext";
import { Helmet } from "react-helmet";

const UserSchema = Yup.object().shape({
  name: Yup.string().min(2).max(50).required("Obrigatório"),
  companyName: Yup.string().min(2).max(50).required("Obrigatório"),
  password: Yup.string().min(5).max(50),
  email: Yup.string().email("Email inválido").required("Obrigatório"),
  phone: Yup.string().required("Obrigatório"),
  planId: Yup.string().required("Selecione um plano"),
});

const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

// resolve URL vinda do backend (relativa ou absoluta) com fallback
const resolveImageUrl = (value, fallback) => {
  if (!value) return fallback;
  if (value.startsWith("http")) return value;
  if (!backendUrl) return value;
  const base = backendUrl.replace(/\/+$/, "");
  const clean = value.replace(/^\/+/, "");
  return `${base}/${clean}`;
};

const calculatePasswordStrength = (password) => {
  let strength = 0;
  if (password.length >= 8) strength += 1;
  if (/[A-Z]/.test(password)) strength += 1;
  if (/[a-z]/.test(password)) strength += 1;
  if (/[0-9]/.test(password)) strength += 1;
  if (/[^A-Za-z0-9]/.test(password)) strength += 1;
  return strength;
};

function SignUp() {
  const { colorMode } = useContext(ColorModeContext);
  const { appLogoFavicon, appName } = colorMode;
  const history = useHistory();
  const { getPlanList } = usePlans();
  
  const [plans, setPlans] = useState([]);
  const [openPlans, setOpenPlans] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [formikHelpers, setFormikHelpers] = useState(null);
  const [focusedInput, setFocusedInput] = useState(null);
  const [isCardHover, setIsCardHover] = useState(false);
  const [isSignupHover, setIsSignupHover] = useState(false);
  const [isLoginHover, setIsLoginHover] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [hoveredPlanId, setHoveredPlanId] = useState(null);
  const [userCreationEnabled, setUserCreationEnabled] = useState(true);

  // Estados para WhatsApp Widget
  const [showWhatsAppMenu, setShowWhatsAppMenu] = useState(false);
  const [isWhatsAppHover, setIsWhatsAppHover] = useState(false);
  const [showBalloon, setShowBalloon] = useState(false);

  // NOVOS ESTADOS para customização
  const [backgroundImage, setBackgroundImage] = useState("");
  const [logoImage, setLogoImage] = useState("");
  const [whatsappNumber, setWhatsappNumber] = useState("");

  const params = qs.parse(window.location.search);
  const companyId = params.companyId || null;

  const initialState = {
    name: "",
    email: "",
    password: "",
    phone: "",
    companyId,
    companyName: "",
    planId: "",
  };

  const backendUrl =
    process.env.REACT_APP_BACKEND_URL === "https://localhost:8090"
      ? "https://localhost:8090"
      : process.env.REACT_APP_BACKEND_URL;
// useEffect para resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

// useEffect para buscar configurações de customização da API
useEffect(() => {
  const fetchCustomizationSettings = async () => {
    try {
      const { data } = await openApi.get("/global-config/public-branding");

      if (data.loginBackground) {
        setBackgroundImage(data.loginBackground);
      }
      
      if (data.loginLogo) {
        setLogoImage(data.loginLogo);
      }
      
      if (data.loginWhatsapp) {
        setWhatsappNumber(data.loginWhatsapp.replace(/[^\d]/g, ''));
      }

      // Buscar número do WhatsApp (se não vier do endpoint acima)
      const whatsappResponse = await fetch(`${backendUrl}/settings/whatsappNumber`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      
      if (whatsappResponse.ok) {
        const whatsappData = await whatsappResponse.json();
        if (whatsappData.whatsappNumber) {
          setWhatsappNumber(whatsappData.whatsappNumber);
        }
      }
    } catch (error) {
      console.error("Erro ao buscar configurações:", error);
    }
  };

  fetchCustomizationSettings();
}, [backendUrl]);
  // useEffect para verificar se cadastro está habilitado
  useEffect(() => {
    const fetchUserCreationStatus = async () => {
      try {
        const response = await fetch(`${backendUrl}/settings/userCreation`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch user creation status");
        }

        const data = await response.json();
        const isEnabled = data.userCreation === "enabled";
        setUserCreationEnabled(isEnabled);

        if (!isEnabled) {
          toast.info("Cadastro de novos usuários está desabilitado.");
          history.push("/login");
        }
      } catch (err) {
        console.error("Erro ao verificar userCreation:", err);
        setUserCreationEnabled(false);
        toast.error("Erro ao verificar permissão de cadastro.");
        history.push("/login");
      }
    };

    fetchUserCreationStatus();
  }, [backendUrl, history]);

  // useEffect para buscar planos
  useEffect(() => {
    const fetchData = async () => {
      try {
        const planList = await getPlanList({ listPublic: "false" });
        setPlans(planList);
      } catch (error) {
        toastError(error);
      }
    };
    fetchData();
  }, [getPlanList]);

  // useEffect para balão do WhatsApp
  useEffect(() => {
    const showBalloonInterval = setInterval(() => {
      if (!showWhatsAppMenu) {
        setShowBalloon(true);
        setTimeout(() => setShowBalloon(false), 5000);
      }
    }, 15000);

    return () => clearInterval(showBalloonInterval);
  }, [showWhatsAppMenu]);
// Funções auxiliares
  const updatePasswordStrength = (password) => {
    setPasswordStrength(calculatePasswordStrength(password));
  };

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  const handleSignUp = async (values) => {
    setLoading(true);
    try {
      await openApi.post("/auth/signup", values);
      setShowSuccessModal(true);
      setTimeout(() => {
        setShowSuccessModal(false);
        setLoading(false);
        history.push("/login");
      }, 8000);
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  };

  const handleClosePlans = () => setOpenPlans(false);

  const handlePlanSelect = (planId) => {
    setSelectedPlan(planId);
    if (formikHelpers) {
      formikHelpers.setFieldValue("planId", planId);
    }
    handleClosePlans();
  };

  const handleGoToLogin = () => {
    setShowSuccessModal(false);
    setLoading(false);
    history.push("/login");
  };

  // Handler para WhatsApp - agora usa o número da API
  const handleWhatsAppClick = (option) => {
    const phone = whatsappNumber || "5592986143081"; // Fallback para número padrão
    let message = "";
    
    switch(option) {
      case "compras":
        message = "Olá! Gostaria de fazer uma compra.";
        break;
      case "suporte":
        message = "Olá! Preciso de suporte técnico.";
        break;
      case "financeiro":
        message = "Olá! Tenho uma dúvida financeira.";
        break;
      case "consultor":
        message = "Olá! Gostaria de falar com um consultor.";
        break;
      default:
        message = "Olá!";
    }
    
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
    setShowWhatsAppMenu(false);
  };

  const getPasswordStrengthColor = (strength) => {
    if (strength <= 2) return "#f44336";
    if (strength <= 4) return "#ff9800";
    return "#4caf50";
  };

  const getPasswordStrengthText = (strength) => {
    if (strength <= 2) return "Senha fraca";
    if (strength > 2 && strength <= 4) return "Senha média";
    return "Senha forte";
  };

// Se cadastro não está habilitado, não renderiza nada
  if (!userCreationEnabled) {
    return null;
  }

  // Determina se deve usar background customizado ou padrão
  // Considera como "sem background" se for null, vazio, ou o caminho padrão do sistema
  const useCustomBackground = backgroundImage 
    && backgroundImage.trim() !== '' 
    && !backgroundImage.includes('login-background-default.png');

  const globalStyles = `
    @keyframes float {
      0%, 100% { transform: translateY(0px) translateX(0px); }
      25% { transform: translateY(-20px) translateX(10px); }
      50% { transform: translateY(-10px) translateX(-10px); }
      75% { transform: translateY(-30px) translateX(5px); }
    }
    
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    
    @keyframes pulse {
      0%, 100% { 
        transform: scale(1);
        opacity: 0.8;
      }
      50% { 
        transform: scale(1.3);
        opacity: 1;
      }
    }
    
    @keyframes fadeIn {
      from {
        opacity: 0;
        transform: translateY(20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes shimmer {
      0% { background-position: -1000px 0; }
      100% { background-position: 1000px 0; }
    }

    @keyframes slideUp {
      from {
        opacity: 0;
        transform: translateY(10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes scaleIn {
      from {
        transform: scale(0);
      }
      to {
        transform: scale(1);
      }
    }

    @keyframes bounceIn {
      0% {
        opacity: 0;
        transform: scale(0.3) translateY(20px);
      }
      50% {
        opacity: 1;
        transform: scale(1.05);
      }
      70% {
        transform: scale(0.9);
      }
      100% {
        transform: scale(1) translateY(0);
      }
    }
  `;
return (
    <>
      <Helmet>
        <title>{appName || "CHATPAGEPRO"}</title>
        <link rel="icon" href={appLogoFavicon || "/default-favicon.ico"} />
        <style>{globalStyles}</style>
      </Helmet>
<div style={{
        background: useCustomBackground 
          ? `url('${resolveImageUrl(backgroundImage, "")}')` 
          : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        backgroundSize: useCustomBackground ? 'cover' : 'auto',
        backgroundPosition: useCustomBackground ? 'center center' : 'initial',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        position: 'relative',
        overflow: 'hidden',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      }}>
{/* Animated Lights Background - Apenas se NÃO tiver background customizado */}
        {!useCustomBackground && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 0,
          }}>
            <div style={{
              position: 'absolute',
              borderRadius: '50%',
              filter: 'blur(60px)',
              animation: 'float 6s ease-in-out infinite',
              width: '300px',
              height: '300px',
              background: 'radial-gradient(circle, rgba(99, 102, 241, 0.25) 0%, rgba(99, 102, 241, 0.12) 40%, transparent 70%)',
              top: '15%',
              left: '5%',
              animationDelay: '0s',
            }}></div>
            <div style={{
              position: 'absolute',
              borderRadius: '50%',
              filter: 'blur(60px)',
              animation: 'float 6s ease-in-out infinite',
              width: '250px',
              height: '250px',
              background: 'radial-gradient(circle, rgba(236, 72, 153, 0.22) 0%, rgba(236, 72, 153, 0.1) 50%, transparent 70%)',
              top: '55%',
              right: '10%',
              animationDelay: '2s',
            }}></div>
            <div style={{
              position: 'absolute',
              borderRadius: '50%',
              filter: 'blur(60px)',
              animation: 'float 6s ease-in-out infinite',
              width: '200px',
              height: '200px',
              background: 'radial-gradient(circle, rgba(59, 130, 246, 0.2) 0%, rgba(59, 130, 246, 0.08) 50%, transparent 70%)',
              bottom: '25%',
              left: '15%',
              animationDelay: '4s',
            }}></div>
            <div style={{
              position: 'absolute',
              borderRadius: '50%',
              filter: 'blur(60px)',
              animation: 'float 6s ease-in-out infinite',
              width: '220px',
              height: '220px',
              background: 'radial-gradient(circle, rgba(168, 85, 247, 0.2) 0%, rgba(168, 85, 247, 0.08) 50%, transparent 70%)',
              top: '40%',
              left: '50%',
              animationDelay: '1s',
            }}></div>
            <div style={{
              position: 'absolute',
              borderRadius: '50%',
              filter: 'blur(60px)',
              animation: 'float 6s ease-in-out infinite',
              width: '280px',
              height: '280px',
              background: 'radial-gradient(circle, rgba(14, 165, 233, 0.22) 0%, rgba(14, 165, 233, 0.1) 50%, transparent 70%)',
              bottom: '10%',
              right: '30%',
              animationDelay: '3s',
            }}></div>
            <div style={{
              position: 'absolute',
              borderRadius: '50%',
              filter: 'blur(60px)',
              animation: 'float 6s ease-in-out infinite',
              width: '240px',
              height: '240px',
              background: 'radial-gradient(circle, rgba(244, 114, 182, 0.2) 0%, rgba(244, 114, 182, 0.09) 50%, transparent 70%)',
              top: '5%',
              right: '25%',
              animationDelay: '5s',
            }}></div>
          </div>
        )}

{/* Geometric Shapes - Apenas se NÃO tiver background customizado */}
        {!useCustomBackground && !isMobile && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 1,
          }}>
            {/* Quadrados girando */}
            <div style={{
              position: 'absolute',
              width: '80px',
              height: '80px',
              border: '1px solid rgba(99, 102, 241, 0.5)',
              borderColor: 'rgba(99, 102, 241, 0.5)',
              background: 'transparent',
              animation: 'spin 12s linear infinite',
              filter: 'drop-shadow(0 0 8px rgba(99, 102, 241, 0.5))',
              top: '15%',
              left: '8%',
            }}></div>
            <div style={{
              position: 'absolute',
              width: '80px',
              height: '80px',
              border: '1px solid rgba(236, 72, 153, 0.5)',
              borderColor: 'rgba(236, 72, 153, 0.5)',
              background: 'transparent',
              animation: 'spin 15s linear infinite reverse',
              filter: 'drop-shadow(0 0 8px rgba(236, 72, 153, 0.5))',
              top: '70%',
              right: '12%',
            }}></div>
            <div style={{
              position: 'absolute',
              width: '80px',
              height: '80px',
              border: '1px solid rgba(14, 165, 233, 0.45)',
              borderColor: 'rgba(14, 165, 233, 0.45)',
              background: 'transparent',
              animation: 'spin 10s linear infinite',
              filter: 'drop-shadow(0 0 8px rgba(14, 165, 233, 0.45))',
              bottom: '25%',
              left: '25%',
            }}></div>
<div style={{
              position: 'absolute',
              width: '80px',
              height: '80px',
              border: '1px solid rgba(168, 85, 247, 0.45)',
              borderColor: 'rgba(168, 85, 247, 0.45)',
              background: 'transparent',
              animation: 'spin 18s linear infinite reverse',
              filter: 'drop-shadow(0 0 8px rgba(168, 85, 247, 0.45))',
              top: '45%',
              right: '8%',
            }}></div>
            <div style={{
              position: 'absolute',
              width: '80px',
              height: '80px',
              border: '1px solid rgba(59, 130, 246, 0.48)',
              borderColor: 'rgba(59, 130, 246, 0.48)',
              background: 'transparent',
              animation: 'spin 14s linear infinite',
              filter: 'drop-shadow(0 0 8px rgba(59, 130, 246, 0.48))',
              top: '8%',
              right: '35%',
            }}></div>

            {/* Círculos pulsando */}
            <div style={{
              position: 'absolute',
              width: '25px',
              height: '25px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.4), rgba(99, 102, 241, 0.25))',
              animation: 'pulse 5s ease-in-out infinite',
              filter: 'drop-shadow(0 0 12px rgba(99, 102, 241, 0.4))',
              top: '25%',
              right: '20%',
              animationDelay: '0s',
            }}></div>
            <div style={{
              position: 'absolute',
              width: '35px',
              height: '35px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.4), rgba(236, 72, 153, 0.25))',
              animation: 'pulse 6s ease-in-out infinite',
              filter: 'drop-shadow(0 0 12px rgba(236, 72, 153, 0.4))',
              bottom: '35%',
              right: '25%',
              animationDelay: '1s',
            }}></div>
            <div style={{
              position: 'absolute',
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.35), rgba(14, 165, 233, 0.2))',
              animation: 'pulse 4.5s ease-in-out infinite',
              filter: 'drop-shadow(0 0 12px rgba(14, 165, 233, 0.35))',
              top: '60%',
              left: '15%',
              animationDelay: '2s',
            }}></div>
            <div style={{
              position: 'absolute',
              width: '30px',
              height: '30px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.38), rgba(168, 85, 247, 0.23))',
              animation: 'pulse 5.5s ease-in-out infinite',
              filter: 'drop-shadow(0 0 12px rgba(168, 85, 247, 0.38))',
              top: '35%',
              left: '40%',
              animationDelay: '0.5s',
            }}></div>
            <div style={{
              position: 'absolute',
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.32), rgba(59, 130, 246, 0.18))',
              animation: 'pulse 4.8s ease-in-out infinite',
              filter: 'drop-shadow(0 0 12px rgba(59, 130, 246, 0.32))',
              bottom: '15%',
              left: '35%',
              animationDelay: '1.5s',
            }}></div>
            <div style={{
              position: 'absolute',
              width: '22px',
              height: '22px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(244, 114, 182, 0.35), rgba(244, 114, 182, 0.2))',
              animation: 'pulse 5.2s ease-in-out infinite',
              filter: 'drop-shadow(0 0 12px rgba(244, 114, 182, 0.35))',
              top: '12%',
              left: '60%',
              animationDelay: '2.5s',
            }}></div>
            <div style={{
              position: 'absolute',
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.37), rgba(99, 102, 241, 0.22))',
              animation: 'pulse 6.2s ease-in-out infinite',
              filter: 'drop-shadow(0 0 12px rgba(99, 102, 241, 0.37))',
              bottom: '45%',
              right: '40%',
              animationDelay: '3s',
            }}></div>
          </div>
        )}

        <div style={{
          width: '100%',
          maxWidth: '32rem',
          position: 'relative',
          zIndex: 10,
        }}>
          {/* Signup Card */}
          <div 
            style={{
              background: isCardHover ? 'rgba(255, 255, 255, 1)' : 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              border: `1px solid ${isCardHover ? 'rgba(59, 130, 246, 0.4)' : 'rgba(59, 130, 246, 0.2)'}`,
              transition: 'all 0.3s ease',
              position: 'relative',
              zIndex: 10,
              boxShadow: isCardHover ? '0 20px 50px rgba(59, 130, 246, 0.25)' : '0 10px 40px rgba(37, 99, 235, 0.15)',
              borderRadius: '1rem',
              padding: isMobile ? '2rem' : '2rem',
              transform: isCardHover ? 'translateY(-5px)' : 'translateY(0)',
              animation: 'fadeIn 0.8s ease-out',
            }}
            onMouseEnter={() => setIsCardHover(true)}
            onMouseLeave={() => setIsCardHover(false)}
          >
{/* Header com Logo personalizada */}
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              {logoImage ? (
                <div style={{ marginBottom: '1.5rem' }}>
                  <img 
                    src={resolveImageUrl(logoImage, "/logo.png")} 
                    alt="Logo" 
                    style={{
                      display: 'block',
                      margin: '0 auto',
                      maxWidth: isMobile ? '180px' : '250px',  // Menor no mobile, maior no desktop
                      height: 'auto',
                    }}
                  />
                </div>
              ) : (
                <div style={{ marginBottom: '1.5rem' }}>
                  <h1 style={{
                    fontSize: '1.875rem',
                    fontWeight: 'bold',
                    background: 'linear-gradient(to right, #2563eb, #3b82f6)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    marginBottom: '0.5rem',
                  }}>
                    Dtalk Pro
                  </h1>
                  <div style={{
                    width: '5rem',
                    height: '0.25rem',
                    background: 'linear-gradient(to right, #2563eb, #3b82f6)',
                    margin: '0 auto',
                    borderRadius: '9999px',
                  }}></div>
                </div>
              )}
              <h2 style={{
                fontSize: '1.25rem',
                fontWeight: '600',
                color: '#1f2937',
                marginBottom: '0.5rem',
              }}>
                Criar Conta
              </h2>
              <p style={{
                color: '#6b7280',
                fontSize: '0.875rem',
              }}>
                Preencha os dados para começar
              </p>
            </div>
{/* Signup Form */}
            <Formik
              initialValues={initialState}
              validationSchema={UserSchema}
              onSubmit={handleSignUp}
            >
              {({ touched, errors, isSubmitting, setFieldValue, values }) => {
                if (!formikHelpers) {
                  setFormikHelpers({ setFieldValue });
                }
                
                return (
                  <Form style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

                    {/* Nome da Empresa */}
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '0.5rem',
                      }}>
                        Nome da Empresa
                      </label>
                      <div style={{
                        position: 'relative',
                        transform: focusedInput === 'companyName' ? 'scale(1.02)' : 'scale(1)',
                        transition: 'transform 0.2s',
                      }}>
                        <div style={{
                          position: 'absolute',
                          left: '0.75rem',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#9ca3af',
                          display: 'flex',
                          alignItems: 'center',
                        }}>
                          <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        </div>
                        <Field
                          name="companyName"
                          type="text"
                          placeholder="Sua empresa"
                          style={{
                            width: '100%',
                            paddingLeft: '3rem',
                            paddingRight: '1rem',
                            paddingTop: '0.75rem',
                            paddingBottom: '0.75rem',
                            background: '#f9fafb',
                            border: focusedInput === 'companyName' ? '1px solid #3b82f6' : '1px solid #d1d5db',
                            borderRadius: '0.5rem',
                            color: '#1f2937',
                            transition: 'all 0.2s',
                            outline: 'none',
                            boxShadow: focusedInput === 'companyName' ? '0 0 0 3px rgba(59, 130, 246, 0.2)' : 'none',
                            boxSizing: 'border-box',
                          }}
                          onFocus={() => setFocusedInput('companyName')}
                          onBlur={() => setFocusedInput(null)}
                        />
                      </div>
                      {touched.companyName && errors.companyName && (
                        <div style={{ color: '#f87171', fontSize: '0.75rem', marginTop: '0.25rem' }}>{errors.companyName}</div>
                      )}
                    </div>

                    {/* Nome e Telefone - Lado a lado */}
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
                      gap: isMobile ? '1.25rem' : '1rem' 
                    }}>
                      {/* Nome */}
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          color: '#374151',
                          marginBottom: '0.5rem',
                        }}>
                          Nome Completo
                        </label>
                        <div style={{
                          position: 'relative',
                          transform: focusedInput === 'name' ? 'scale(1.02)' : 'scale(1)',
                          transition: 'transform 0.2s',
                        }}>
                          <div style={{
                            position: 'absolute',
                            left: '0.75rem',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#9ca3af',
                            display: 'flex',
                            alignItems: 'center',
                          }}>
                            <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          </div>
                          <Field
                            name="name"
                            type="text"
                            placeholder="Seu nome"
                            style={{
                              width: '100%',
                              paddingLeft: '3rem',
                              paddingRight: '1rem',
                              paddingTop: '0.75rem',
                              paddingBottom: '0.75rem',
                              background: '#f9fafb',
                              border: focusedInput === 'name' ? '1px solid #3b82f6' : '1px solid #d1d5db',
                              borderRadius: '0.5rem',
                              color: '#1f2937',
                              transition: 'all 0.2s',
                              outline: 'none',
                              boxShadow: focusedInput === 'name' ? '0 0 0 3px rgba(59, 130, 246, 0.2)' : 'none',
                              boxSizing: 'border-box',
                            }}
                            onFocus={() => setFocusedInput('name')}
                            onBlur={() => setFocusedInput(null)}
                          />
                        </div>
                        {touched.name && errors.name && (
                          <div style={{ color: '#f87171', fontSize: '0.75rem', marginTop: '0.25rem' }}>{errors.name}</div>
                        )}
                      </div>

                      {/* Telefone */}
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          color: '#374151',
                          marginBottom: '0.5rem',
                        }}>
                          Telefone
                        </label>
                        <div style={{
                          position: 'relative',
                          transform: focusedInput === 'phone' ? 'scale(1.02)' : 'scale(1)',
                          transition: 'transform 0.2s',
                        }}>
                          <div style={{
                            position: 'absolute',
                            left: '0.75rem',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#9ca3af',
                            display: 'flex',
                            alignItems: 'center',
                          }}>
                            <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                          </div>
                          <Field
                            name="phone"
                            type="tel"
                            placeholder="(11) 99999-9999"
                            style={{
                              width: '100%',
                              paddingLeft: '3rem',
                              paddingRight: '1rem',
                              paddingTop: '0.75rem',
                              paddingBottom: '0.75rem',
                              background: '#f9fafb',
                              border: focusedInput === 'phone' ? '1px solid #3b82f6' : '1px solid #d1d5db',
                              borderRadius: '0.5rem',
                              color: '#1f2937',
                              transition: 'all 0.2s',
                              outline: 'none',
                              boxShadow: focusedInput === 'phone' ? '0 0 0 3px rgba(59, 130, 246, 0.2)' : 'none',
                              boxSizing: 'border-box',
                            }}
                            onFocus={() => setFocusedInput('phone')}
                            onBlur={() => setFocusedInput(null)}
                          />
                        </div>
                        {touched.phone && errors.phone && (
                          <div style={{ color: '#f87171', fontSize: '0.75rem', marginTop: '0.25rem' }}>{errors.phone}</div>
                        )}
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '0.5rem',
                      }}>
                        Email
                      </label>
                      <div style={{
                        position: 'relative',
                        transform: focusedInput === 'email' ? 'scale(1.02)' : 'scale(1)',
                        transition: 'transform 0.2s',
                      }}>
                        <div style={{
                          position: 'absolute',
                          left: '0.75rem',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#9ca3af',
                          display: 'flex',
                          alignItems: 'center',
                        }}>
                          <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                          </svg>
                        </div>
                        <Field
                          name="email"
                          type="email"
                          placeholder="seu@email.com"
                          style={{
                            width: '100%',
                            paddingLeft: '3rem',
                            paddingRight: '1rem',
                            paddingTop: '0.75rem',
                            paddingBottom: '0.75rem',
                            background: '#f9fafb',
                            border: focusedInput === 'email' ? '1px solid #3b82f6' : '1px solid #d1d5db',
                            borderRadius: '0.5rem',
                            color: '#1f2937',
                            transition: 'all 0.2s',
                            outline: 'none',
                            boxShadow: focusedInput === 'email' ? '0 0 0 3px rgba(59, 130, 246, 0.2)' : 'none',
                            boxSizing: 'border-box',
                          }}
                          onFocus={() => setFocusedInput('email')}
                          onBlur={() => setFocusedInput(null)}
                        />
                      </div>
                      {touched.email && errors.email && (
                        <div style={{ color: '#f87171', fontSize: '0.75rem', marginTop: '0.25rem' }}>{errors.email}</div>
                      )}
                    </div>
{/* Senha e Plano - Lado a lado */}
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
                      gap: isMobile ? '1.25rem' : '1rem' 
                    }}>
                      {/* Senha */}
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          color: '#374151',
                          marginBottom: '0.5rem',
                        }}>
                          Senha
                        </label>
                        <div style={{
                          position: 'relative',
                          transform: focusedInput === 'password' ? 'scale(1.02)' : 'scale(1)',
                          transition: 'transform 0.2s',
                        }}>
                          <div style={{
                            position: 'absolute',
                            left: '0.75rem',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#9ca3af',
                            display: 'flex',
                            alignItems: 'center',
                          }}>
                            <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                            </svg>
                          </div>
                          <Field
                            name="password"
                            type={passwordVisible ? "text" : "password"}
                            placeholder="••••••••"
                            style={{
                              width: '100%',
                              paddingLeft: '3rem',
                              paddingRight: '3rem',
                              paddingTop: '0.75rem',
                              paddingBottom: '0.75rem',
                              background: '#f9fafb',
                              border: focusedInput === 'password' ? '1px solid #3b82f6' : '1px solid #d1d5db',
                              borderRadius: '0.5rem',
                              color: '#1f2937',
                              transition: 'all 0.2s',
                              outline: 'none',
                              boxShadow: focusedInput === 'password' ? '0 0 0 3px rgba(59, 130, 246, 0.2)' : 'none',
                              boxSizing: 'border-box',
                            }}
                            onFocus={() => setFocusedInput('password')}
                            onBlur={() => setFocusedInput(null)}
                            onChange={(e) => {
                              setFieldValue("password", e.target.value);
                              updatePasswordStrength(e.target.value);
                            }}
                          />
                          <button
                            type="button"
                            onClick={togglePasswordVisibility}
                            style={{
                              position: 'absolute',
                              right: '0.75rem',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '0.25rem',
                              display: 'flex',
                              alignItems: 'center',
                              color: '#6b7280',
                            }}
                          >
                            {passwordVisible ? (
                              <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"></path>
                              </svg>
                            ) : (
                              <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                              </svg>
                            )}
                          </button>
                        </div>
                        
                        {/* Barra de força da senha */}
                        {values.password && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                            <div style={{
                              flex: 1,
                              height: '3px',
                              backgroundColor: '#e0e0e0',
                              borderRadius: '2px',
                              overflow: 'hidden',
                            }}>
                              <div style={{
                                height: '100%',
                                borderRadius: '2px',
                                transition: 'all 0.3s ease',
                                width: `${(passwordStrength / 5) * 100}%`,
                                backgroundColor: getPasswordStrengthColor(passwordStrength)
                              }} />
                            </div>
                            <span style={{
                              fontSize: '0.7rem',
                              fontWeight: '500',
                              color: getPasswordStrengthColor(passwordStrength),
                              whiteSpace: 'nowrap',
                            }}>
                              {getPasswordStrengthText(passwordStrength)}
                            </span>
                          </div>
                        )}
                        
                        {touched.password && errors.password && (
                          <div style={{ color: '#f87171', fontSize: '0.75rem', marginTop: '0.25rem' }}>{errors.password}</div>
                        )}
                      </div>

                      {/* Plano */}
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          color: '#374151',
                          marginBottom: '0.5rem',
                        }}>
                          Plano
                        </label>
                        <div style={{
                          position: 'relative',
                          transform: focusedInput === 'planId' ? 'scale(1.02)' : 'scale(1)',
                          transition: 'transform 0.2s',
                        }}>
                          <div style={{
                            position: 'absolute',
                            left: '0.75rem',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#9ca3af',
                            display: 'flex',
                            alignItems: 'center',
                            pointerEvents: 'none',
                            zIndex: 1,
                          }}>
                            <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 1.343-3 3s1.343 3 3 3 3-1.343 3-3-1.343-3-3-3z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.745 3.745 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.745 3.745 0 013.296-1.043A3.745 3.745 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.745 3.745 0 013.296 1.043 3.745 3.745 0 011.043 3.296A3.745 3.745 0 0121 12z" />
                            </svg>
                          </div>
                          <button
                            type="button"
                            onClick={() => setOpenPlans(true)}
                            onFocus={() => setFocusedInput('planId')}
                            onBlur={() => setFocusedInput(null)}
                            style={{
                              width: '100%',
                              paddingLeft: '3rem',
                              paddingRight: '3rem',
                              paddingTop: '0.75rem',
                              paddingBottom: '0.75rem',
                              background: '#f9fafb',
                              border: focusedInput === 'planId' ? '1px solid #3b82f6' : '1px solid #d1d5db',
                              borderRadius: '0.5rem',
                              color: selectedPlan ? '#1f2937' : '#9ca3af',
                              transition: 'all 0.2s',
                              outline: 'none',
                              boxShadow: focusedInput === 'planId' ? '0 0 0 3px rgba(59, 130, 246, 0.2)' : 'none',
                              boxSizing: 'border-box',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              textAlign: 'left',
                            }}
                          >
                            <span style={{ 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis', 
                              whiteSpace: 'nowrap' 
                            }}>
                              {selectedPlan
                                ? plans.find((p) => p.id === selectedPlan)?.name || "Escolher Plano"
                                : "Escolher Plano"}
                            </span>
                            <svg style={{ width: '1rem', height: '1rem', color: '#9ca3af', flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                        </div>
                        {touched.planId && errors.planId && (
                          <div style={{ color: '#f87171', fontSize: '0.75rem', marginTop: '0.25rem' }}>{errors.planId}</div>
                        )}
                      </div>
                    </div>

                    {/* Signup Button */}
                    <button
                      type="submit"
                      disabled={loading || isSubmitting}
                      style={{
                        width: '100%',
                        background: 'linear-gradient(to right, #2563eb, #3b82f6)',
                        color: 'white',
                        padding: '0.75rem 1rem',
                        borderRadius: '0.5rem',
                        fontWeight: '500',
                        transition: 'all 0.2s',
                        border: 'none',
                        cursor: (loading || isSubmitting) ? 'not-allowed' : 'pointer',
                        outline: 'none',
                        transform: isSignupHover && !loading && !isSubmitting ? 'translateY(-1px)' : 'translateY(0)',
                        boxShadow: isSignupHover && !loading && !isSubmitting ? '0 10px 25px rgba(59, 130, 246, 0.4)' : 'none',
                        opacity: (loading || isSubmitting) ? 0.6 : 1,
                      }}
                      onMouseEnter={() => setIsSignupHover(true)}
                      onMouseLeave={() => setIsSignupHover(false)}
                    >
                      {loading || isSubmitting ? "Criando conta..." : (i18n.t("signup.buttons.submit") || "Criar Conta")}
                    </button>
                  </Form>
                );
              }}
            </Formik>

            {/* Login Link */}
            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
              <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                Já tem uma conta?{' '}
                <RouterLink
                  to="/login"
                  style={{
                    color: isLoginHover ? '#2563eb' : '#3b82f6',
                    fontWeight: '500',
                    transition: 'color 0.2s',
                    textDecoration: 'none',
                  }}
                  onMouseEnter={() => setIsLoginHover(true)}
                  onMouseLeave={() => setIsLoginHover(false)}
                >
                  {i18n.t("signup.buttons.login") || "Fazer login"}
                </RouterLink>
              </p>
            </div>
          </div>
        </div>
{/* WhatsApp Widget */}
        <div style={{
          position: 'fixed',
          bottom: '2rem',
          right: '2rem',
          zIndex: 1000,
        }}>
          {/* Balão "Estamos online" */}
          {showBalloon && !showWhatsAppMenu && (
            <div style={{
              position: 'absolute',
              bottom: '80px',
              right: '0',
              background: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(20px)',
              borderRadius: '1rem',
              padding: '1rem 1.25rem',
              boxShadow: '0 10px 40px rgba(37, 99, 235, 0.25)',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              minWidth: '200px',
              animation: 'bounceIn 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
              cursor: 'pointer',
            }}
            onClick={() => {
              setShowBalloon(false);
              setShowWhatsAppMenu(true);
            }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
              }}>
                <div style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  background: '#25D366',
                  boxShadow: '0 0 10px rgba(37, 211, 102, 0.6)',
                  animation: 'pulse 2s ease-in-out infinite',
                }}></div>
                <div>
                  <div style={{
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: '#1f2937',
                    marginBottom: '0.125rem',
                  }}>
                    Olá! Estamos online.
                  </div>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#6b7280',
                  }}>
                    Como podemos ajudar?
                  </div>
                </div>
              </div>
              {/* Seta apontando para o botão */}
              <div style={{
                position: 'absolute',
                bottom: '-8px',
                right: '20px',
                width: 0,
                height: 0,
                borderLeft: '8px solid transparent',
                borderRight: '8px solid transparent',
                borderTop: '8px solid rgba(255, 255, 255, 0.98)',
                filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))',
              }}></div>
            </div>
          )}

          {/* Menu de opções com cabeçalho moderno */}
          {showWhatsAppMenu && (
            <div style={{
              position: 'absolute',
              bottom: '80px',
              right: '0',
              background: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(20px)',
              borderRadius: '1rem',
              boxShadow: '0 10px 40px rgba(37, 99, 235, 0.2)',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              width: '320px',
              maxHeight: '480px',
              overflow: 'hidden',
              animation: 'slideUp 0.3s ease-out',
            }}>
              {/* Cabeçalho moderno */}
              <div style={{
                background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                padding: '1rem 1.25rem',
                color: 'white',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  marginBottom: '0.5rem',
                }}>
                  <div style={{
                    width: '42px',
                    height: '42px',
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                  }}>
                    <svg 
                      style={{ 
                        width: '24px', 
                        height: '24px',
                      }} 
                      fill="white" 
                      viewBox="0 0 24 24"
                    >
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: '1.0625rem',
                      fontWeight: '700',
                      marginBottom: '0.25rem',
                    }}>
                      Atendimento
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontSize: '0.8125rem',
                      opacity: 0.95,
                    }}>
                      <div style={{
                        width: '7px',
                        height: '7px',
                        borderRadius: '50%',
                        background: '#fff',
                        boxShadow: '0 0 8px rgba(255, 255, 255, 0.8)',
                        animation: 'pulse 2s ease-in-out infinite',
                      }}></div>
                      <span>Estamos online</span>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowWhatsAppMenu(false)}
                    style={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      border: 'none',
                      borderRadius: '50%',
                      width: '30px',
                      height: '30px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                      e.currentTarget.style.transform = 'rotate(90deg)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                      e.currentTarget.style.transform = 'rotate(0deg)';
                    }}
                  >
                    <svg style={{ width: '18px', height: '18px' }} fill="white" viewBox="0 0 24 24">
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                    </svg>
                  </button>
                </div>
                <div style={{
                  fontSize: '0.8125rem',
                  opacity: 0.9,
                  lineHeight: 1.4,
                }}>
                  Olá! 👋 Como podemos ajudar você hoje?
                </div>
              </div>

              {/* Opções de contato */}
              <div style={{ padding: '0.875rem 1rem 1rem' }}>
                <div style={{
                  fontSize: '0.6875rem',
                  fontWeight: '500',
                  color: '#94a3b8',
                  marginBottom: '0.75rem',
                  paddingLeft: '0.25rem',
                  letterSpacing: '0.08em',
                }}>
                  Selecione um departamento
                </div>
                {[
                  { 
                    id: 'compras', 
                    label: 'Compras', 
                    icon: (
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: '20px', height: '20px' }}>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/>
                      </svg>
                    ),
                    color: 'rgba(59, 130, 246, 0.1)',
                    iconColor: '#3b82f6'
                  },
                  { 
                    id: 'suporte', 
                    label: 'Suporte Técnico', 
                    icon: (
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: '20px', height: '20px' }}>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                      </svg>
                    ),
                    color: 'rgba(168, 85, 247, 0.1)',
                    iconColor: '#a855f7'
                  },
                  { 
                    id: 'financeiro', 
                    label: 'Financeiro', 
                    icon: (
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: '20px', height: '20px' }}>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                    ),
                    color: 'rgba(34, 197, 94, 0.1)',
                    iconColor: '#22c55e'
                  },
                  { 
                    id: 'consultor', 
                    label: 'Falar com Consultor', 
                    icon: (
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: '20px', height: '20px' }}>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                      </svg>
                    ),
                    color: 'rgba(236, 72, 153, 0.1)',
                    iconColor: '#ec4899'
                  },
                ].map((option, index) => (
                  <button
                    key={option.id}
                    onClick={() => handleWhatsAppClick(option.id)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.875rem',
                      padding: '0.75rem 0.875rem',
                      background: 'transparent',
                      border: '1px solid rgba(226, 232, 240, 0.8)',
                      borderRadius: '0.625rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      fontSize: '0.875rem',
                      color: '#1f2937',
                      fontWeight: '500',
                      textAlign: 'left',
                      marginBottom: index < 3 ? '0.5rem' : '0',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = option.color;
                      e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.3)';
                      e.currentTarget.style.transform = 'translateX(4px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.borderColor = 'rgba(226, 232, 240, 0.8)';
                      e.currentTarget.style.transform = 'translateX(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '0.5rem',
                      background: option.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      color: option.iconColor,
                    }}>
                      {option.icon}
                    </div>
                    <span style={{ flex: 1 }}>{option.label}</span>
                    <svg style={{ width: '18px', height: '18px', opacity: 0.4 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
                    </svg>
                  </button>
                ))}
              </div>

              {/* Footer */}
              <div style={{
                padding: '0.625rem 1rem',
                borderTop: '1px solid rgba(226, 232, 240, 0.8)',
                background: 'rgba(249, 250, 251, 0.5)',
                fontSize: '0.6875rem',
                color: '#6b7280',
                textAlign: 'center',
              }}>
                Tempo de resposta: <span style={{ fontWeight: '600', color: '#25D366' }}>Imediato</span>
              </div>
            </div>
          )}

          {/* Botão principal do WhatsApp */}
          <button
            onClick={() => setShowWhatsAppMenu(!showWhatsAppMenu)}
            onMouseEnter={() => setIsWhatsAppHover(true)}
            onMouseLeave={() => setIsWhatsAppHover(false)}
            style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: isWhatsAppHover 
                ? 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' 
                : 'linear-gradient(135deg, #25D366 0%, #25D366 100%)',
              border: 'none',
              cursor: 'pointer',
              boxShadow: isWhatsAppHover 
                ? '0 12px 35px rgba(37, 211, 102, 0.5)' 
                : '0 8px 25px rgba(37, 211, 102, 0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: isWhatsAppHover ? 'scale(1.1) rotate(10deg)' : 'scale(1) rotate(0deg)',
              animation: 'scaleIn 0.5s ease-out',
              position: 'relative',
            }}
          >
            {/* Pulse effect */}
            <div style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              background: 'rgba(37, 211, 102, 0.4)',
              animation: 'pulse 2s ease-in-out infinite',
            }}></div>
            
            {/* WhatsApp Icon */}
            <svg 
              style={{ 
                width: '36px', 
                height: '36px',
                position: 'relative',
                zIndex: 1,
              }} 
              fill="white" 
              viewBox="0 0 24 24"
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
            </svg>

            {/* Badge de notificação */}
            {showWhatsAppMenu && (
              <div style={{
                position: 'absolute',
                top: '-4px',
                right: '-4px',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                border: '2px solid white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.625rem',
                fontWeight: 'bold',
                color: 'white',
                animation: 'pulse 1.5s ease-in-out infinite',
              }}>
                4
              </div>
            )}
          </button>

          {/* Tooltip */}
          {!showWhatsAppMenu && isWhatsAppHover && (
            <div style={{
              position: 'absolute',
              bottom: '50%',
              right: '80px',
              transform: 'translateY(50%)',
              background: 'rgba(31, 41, 55, 0.95)',
              color: 'white',
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              whiteSpace: 'nowrap',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              animation: 'fadeIn 0.2s ease-out',
            }}>
              Fale conosco no WhatsApp
              <div style={{
                position: 'absolute',
                right: '-6px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: 0,
                height: 0,
                borderTop: '6px solid transparent',
                borderBottom: '6px solid transparent',
                borderLeft: '6px solid rgba(31, 41, 55, 0.95)',
              }}></div>
            </div>
          )}
        </div>
      </div>
      {/* Modal de Planos */}
      {openPlans && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            opacity: 1,
            transition: 'opacity 0.3s ease-in-out',
            padding: '1rem',
          }} 
          onClick={handleClosePlans}
        >
          <div 
            style={{
              background: '#ffffff',
              borderRadius: '8px',
              padding: isMobile ? '1.5rem' : '2.5rem',
              maxWidth: '900px',
              maxHeight: '85vh',
              overflowY: 'auto',
              width: '100%',
              transform: 'scale(1)',
              transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 6.4px 14.4px 0 rgba(0, 0, 0, 0.132), 0 1.2px 3.6px 0 rgba(0, 0, 0, 0.108)',
              animation: 'slideUp 0.4s ease-out',
            }} 
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header do Modal */}
            <div style={{ 
              textAlign: 'center', 
              marginBottom: '2rem',
              paddingBottom: '1.5rem',
              borderBottom: '1px solid #edebe9'
            }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #0078d4 0%, #106ebe 100%)',
                marginBottom: '1rem',
                boxShadow: '0 1.6px 3.6px 0 rgba(0, 0, 0, 0.132), 0 0.3px 0.9px 0 rgba(0, 0, 0, 0.108)',
              }}>
                <svg width="28" height="28" fill="none" stroke="white" viewBox="0 0 24 24" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 1.343-3 3s1.343 3 3 3 3-1.343 3-3-1.343-3-3-3z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.745 3.745 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.745 3.745 0 013.296-1.043A3.745 3.745 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.745 3.745 0 013.296 1.043 3.745 3.745 0 011.043 3.296A3.745 3.745 0 0121 12z" />
                </svg>
              </div>
              <h2 style={{ 
                fontSize: '1.875rem', 
                fontWeight: '600', 
                color: '#323130',
                marginBottom: '0.5rem', 
                margin: '0 0 0.5rem 0',
                fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif",
              }}>
                Escolha seu Plano
              </h2>
              <p style={{ 
                color: '#605e5c', 
                fontSize: '1rem', 
                margin: 0,
                fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif",
              }}>
                Selecione o plano ideal para sua empresa crescer
              </p>
            </div>

            {/* Grid de Planos */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: '1.25rem',
              marginTop: '2rem',
            }}>
              {plans.map((plan) => {
                const isSelected = plan.id === selectedPlan;
                const isHovered = hoveredPlanId === plan.id;
                const isFree = plan.amount === 0 || plan.amount === "0" || plan.name.toLowerCase().includes("gratuito");
                
                return (
                  <div
                    key={plan.id}
                    style={{
                      position: 'relative',
                      border: isSelected ? '2px solid #0078d4' : '1px solid #e5e7eb',
                      borderRadius: '6px',
                      padding: '1.5rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      background: '#ffffff',
                      transform: isHovered && !isSelected ? 'translateY(-2px)' : 'translateY(0)',
                      boxShadow: isSelected 
                        ? '0 4px 12px rgba(0, 120, 212, 0.15)' 
                        : isHovered 
                          ? '0 2px 8px rgba(0, 0, 0, 0.08)' 
                          : '0 1px 3px rgba(0, 0, 0, 0.06)',
                    }}
                    onClick={() => handlePlanSelect(plan.id)}
                    onMouseEnter={() => setHoveredPlanId(plan.id)}
                    onMouseLeave={() => setHoveredPlanId(null)}
                  >
                    {/* Badge Selecionado */}
                    {isSelected && (
                      <div style={{
                        position: 'absolute',
                        top: '0.75rem',
                        right: '0.75rem',
                        background: '#0078d4',
                        color: 'white',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '3px',
                        fontSize: '0.7rem',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
                      }}>
                        <svg width="10" height="10" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        Ativo
                      </div>
                    )}
                    
                    {/* Nome do Plano */}
                    <h3 style={{ 
                      fontSize: '1.125rem', 
                      fontWeight: '600', 
                      color: '#1f2937',
                      marginBottom: '0.5rem', 
                      margin: '0 0 0.5rem 0',
                      paddingRight: isSelected ? '3.5rem' : '0',
                      fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
                    }}>
                      {plan.name}
                    </h3>
                    
                    {/* Preço */}
                    <div style={{ 
                      fontSize: '1.75rem', 
                      fontWeight: '700', 
                      color: '#0078d4',
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: '0.25rem',
                      marginBottom: '1.25rem',
                      fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
                    }}>
                      {isFree ? (
                        <span>Grátis</span>
                      ) : (
                        <>
                          <span style={{ fontSize: '1rem', fontWeight: '600' }}>R$</span>
                          <span>{plan.amount}</span>
                        </>
                      )}
                    </div>
                    
                    {/* Features */}
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: '0.75rem',
                      marginBottom: '1.25rem',
                    }}>
                      {/* Atendentes */}
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.5rem',
                      }}>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '4px',
                          background: '#f0f9ff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}>
                          <svg width="16" height="16" fill="none" stroke="#0078d4" viewBox="0 0 24 24" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                          </svg>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ 
                            fontSize: '0.75rem', 
                            color: '#6b7280',
                            fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
                          }}>
                            Atendentes
                          </div>
                          <div style={{ 
                            fontSize: '0.95rem', 
                            fontWeight: '600', 
                            color: '#1f2937',
                            fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
                          }}>
                            {plan.users}
                          </div>
                        </div>
                      </div>
                      
                      {/* Conexões */}
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.5rem',
                      }}>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '4px',
                          background: '#f0fdf4',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}>
                          <svg width="16" height="16" fill="none" stroke="#10b981" viewBox="0 0 24 24" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ 
                            fontSize: '0.75rem', 
                            color: '#6b7280',
                            fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
                          }}>
                            Conexões
                          </div>
                          <div style={{ 
                            fontSize: '0.95rem', 
                            fontWeight: '600', 
                            color: '#1f2937',
                            fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
                          }}>
                            {plan.connections}
                          </div>
                        </div>
                      </div>
                      
                      {/* Filas */}
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.5rem',
                      }}>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '4px',
                          background: '#faf5ff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}>
                          <svg width="16" height="16" fill="none" stroke="#8b5cf6" viewBox="0 0 24 24" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ 
                            fontSize: '0.75rem', 
                            color: '#6b7280',
                            fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
                          }}>
                            Filas
                          </div>
                          <div style={{ 
                            fontSize: '0.95rem', 
                            fontWeight: '600', 
                            color: '#1f2937',
                            fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
                          }}>
                            {plan.queues}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Botão de Seleção */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePlanSelect(plan.id);
                      }}
                      style={{
                        width: '100%',
                        padding: '0.625rem',
                        background: isSelected 
                          ? '#0078d4' 
                          : 'transparent',
                        color: isSelected ? 'white' : '#0078d4',
                        border: isSelected ? 'none' : '1.5px solid #0078d4',
                        borderRadius: '4px',
                        fontWeight: '600',
                        fontSize: '0.8125rem',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
                        transform: 'scale(1)',
                      }}
                      onMouseEnter={(e) => {
                        if (isSelected) {
                          e.currentTarget.style.background = '#106ebe';
                          e.currentTarget.style.transform = 'scale(1.02)';
                        } else {
                          e.currentTarget.style.background = '#0078d4';
                          e.currentTarget.style.color = 'white';
                          e.currentTarget.style.transform = 'scale(1.02)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (isSelected) {
                          e.currentTarget.style.background = '#0078d4';
                          e.currentTarget.style.transform = 'scale(1)';
                        } else {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.color = '#0078d4';
                          e.currentTarget.style.transform = 'scale(1)';
                        }
                      }}
                      onMouseDown={(e) => {
                        e.currentTarget.style.transform = 'scale(0.98)';
                      }}
                      onMouseUp={(e) => {
                        e.currentTarget.style.transform = 'scale(1.02)';
                      }}
                    >
                      {isSelected ? (
                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.375rem' }}>
                          <svg width="14" height="14" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          Selecionado
                        </span>
                      ) : (
                        'Selecionar'
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
            
            {/* Botão Fechar */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              marginTop: '2rem',
              paddingTop: '1.5rem',
              borderTop: '1px solid #e5e7eb',
            }}>
              <button 
                style={{
                  padding: '0.625rem 1.75rem',
                  background: 'transparent',
                  border: '1.5px solid #6b7280',
                  color: '#374151',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  fontWeight: '600',
                  fontSize: '0.8125rem',
                  fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
                }}
                onClick={handleClosePlans}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f3f4f6';
                  e.currentTarget.style.borderColor = '#374151';
                  e.currentTarget.style.transform = 'scale(1.02)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = '#6b7280';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.transform = 'scale(0.98)';
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.transform = 'scale(1.02)';
                }}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Sucesso */}
      {showSuccessModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            opacity: 1,
            transition: 'opacity 0.3s ease-in-out',
          }}
        >
          <div 
            style={{
              background: 'white',
              borderRadius: '1rem',
              padding: '2rem',
              maxWidth: '450px',
              width: '90%',
              margin: '1rem',
              textAlign: 'center',
              transform: 'scale(1)',
              transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 20px 50px rgba(0, 0, 0, 0.3)',
            }}
          >
            <div style={{ 
              width: '80px', 
              height: '80px', 
              background: 'linear-gradient(to right, #2563eb, #3b82f6)', 
              borderRadius: '50%', 
              margin: '0 auto 1.5rem', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}>
              <svg width="40" height="40" fill="none" stroke="white" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 style={{ 
              fontSize: '1.5rem', 
              fontWeight: 'bold', 
              color: '#1f2937', 
              marginBottom: '1rem', 
              margin: '0 0 1rem 0' 
            }}>
              Conta criada com sucesso!
            </h3>
            <p style={{ 
              color: '#6b7280', 
              marginBottom: '1.5rem', 
              lineHeight: 1.6, 
              margin: '0 0 1.5rem 0' 
            }}>
              Parabéns! Sua conta foi criada com sucesso. Você já pode fazer login e começar a usar nossa plataforma.
            </p>
            <button 
              style={{
                background: 'linear-gradient(to right, #2563eb, #3b82f6)',
                color: 'white',
                padding: '0.75rem 2rem',
                borderRadius: '0.5rem',
                fontWeight: '600',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onClick={handleGoToLogin}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 10px 25px rgba(59, 130, 246, 0.4)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Ir para Login
            </button>
            <p style={{ 
              color: '#9ca3af', 
              fontSize: '0.875rem', 
              marginTop: '1rem', 
              margin: '1rem 0 0 0' 
            }}>
              Redirecionando automaticamente em alguns segundos...
            </p>
          </div>
        </div>
      )}
    </>
  );
}

export default SignUp;